import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UnreadCount {
    [conversationId: string]: number;
}

interface UnreadByUser {
    [userId: string]: number;
}

export const useUnreadMessages = () => {
    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();

    // Real-time subscription for new messages
    useEffect(() => {
        if (!currentUser) return;

        const channel = supabase
            .channel('unread_messages_updates')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: ['unread_messages'] });
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [currentUser, queryClient]);

    const fetchUnreadMessages = async (): Promise<UnreadByUser> => {
        if (!currentUser) return {};

        // Get all conversations where the user is a participant
        const { data: participations, error: partError } = await supabase
            .from('chat_participants')
            .select('conversation_id')
            .eq('user_id', currentUser.id);

        if (partError || !participations?.length) return {};

        const conversationIds = participations.map(p => p.conversation_id);

        // Get the last read timestamp for each conversation (if we have this field)
        // For now, we'll count messages not sent by the current user that are recent (last 24h)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        // Get unread messages (messages not sent by current user, in conversations they're part of)
        const { data: messages, error: msgError } = await supabase
            .from('chat_messages')
            .select('conversation_id, sender_id, created_at')
            .in('conversation_id', conversationIds)
            .neq('sender_id', currentUser.id)
            .gte('created_at', yesterday.toISOString())
            .order('created_at', { ascending: false });

        if (msgError || !messages) return {};

        // Get conversation participants to map sender to user
        const { data: allParticipants } = await supabase
            .from('chat_participants')
            .select('conversation_id, user_id')
            .in('conversation_id', conversationIds);

        // Build unread count by user (the other participant in direct chats)
        const unreadByUser: UnreadByUser = {};

        for (const msg of messages) {
            // Find the other participant in this conversation (not current user, not the sender if they're different)
            const otherParticipants = allParticipants?.filter(
                p => p.conversation_id === msg.conversation_id && p.user_id !== currentUser.id
            );

            if (otherParticipants) {
                for (const participant of otherParticipants) {
                    // Only count if the message was sent by this participant
                    if (msg.sender_id === participant.user_id) {
                        unreadByUser[participant.user_id] = (unreadByUser[participant.user_id] || 0) + 1;
                    }
                }
            }
        }

        return unreadByUser;
    };

    const { data: unreadByUser, isLoading } = useQuery({
        queryKey: ['unread_messages'],
        queryFn: fetchUnreadMessages,
        enabled: !!currentUser,
        refetchInterval: 30000, // Refetch every 30 seconds as backup
    });

    const getUnreadCount = (userId: string): number => {
        return unreadByUser?.[userId] || 0;
    };

    const getTotalUnread = (): number => {
        if (!unreadByUser) return 0;
        return Object.values(unreadByUser).reduce((sum, count) => sum + count, 0);
    };

    return {
        unreadByUser: unreadByUser || {},
        getUnreadCount,
        getTotalUnread,
        isLoading,
    };
};
